import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useCampaign } from '@tm/client-form/src/hooks/useCampaign'
import { useI18N } from '@tm/client-form/src/hooks/useI18n'
import { CustomValue } from '@tm/shared-lib/src/api'
import { CsatField } from '@tm/shared-lib/src/field'
import { InputProps, numberValue } from './types'

type CsatInnerProps = {
  value?: number
  onChange?: (value: CustomValue) => void
  disabled?: boolean
}

const options = [1, 2, 3, 4, 5]

export function CsatInputInner({ value, onChange, disabled }: CsatInnerProps) {
  const { t } = useI18N()
  const { primaryColor, primaryTextColor, campaignFrame } = useCampaign()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const isCompact = campaignFrame.frameView === 'compact'

  return (
    <Stack gap={2} width="100%" mx="auto" maxWidth={isCompact ? 360 : undefined}>
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={{
          xs: 1,
          sm: 0.5,
        }}
        justifyContent={{
          xs: 'center',
          sm: 'space-between',
        }}>
        {options.map(option => {
          const isSelected = option === value
          return (
            <Button
              key={option}
              onClick={() => onChange && onChange(option)}
              color={isSelected ? 'primary' : 'inherit'}
              variant="contained"
              disabled={disabled}
              sx={{
                width: {
                  xs: 'calc(100% / 5 - 4px)',
                  sm: 'calc(100% / 5 - 8px)',
                },
                height: {
                  xs: '36px',
                  sm: '40px',
                },
                px: 0,
                textAlign: 'center',
                ...(isSelected && {
                  backgroundColor: primaryColor,
                  color: primaryTextColor,
                  '&:hover': {
                    backgroundColor: primaryColor,
                  },
                  '&:active': {
                    backgroundColor: primaryColor,
                  },
                }),
              }}>
              {option}
            </Button>
          )
        })}
      </Stack>
      <Stack direction="row" justifyContent={{ xs: 'center', sm: 'space-between' }}>
        {isSmall ? (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {`0 = ${t('csat.label1')}, 5 = ${t('csat.label5')}`}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              {t('csat.label1')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('csat.label5')}
            </Typography>
          </>
        )}
      </Stack>
    </Stack>
  )
}

export function CsatInput(props: InputProps<CsatField>) {
  const { value, onChange, isSubmitting } = props

  return <CsatInputInner value={numberValue(value)} onChange={onChange} disabled={isSubmitting} />
}
